import { CANVAS_HEIGHT, CANVAS_WIDTH } from '@/hooks/constants';
import { useGetCurrentAgentsPresenter } from '@/pages/agents/hooks';
import { displayInPlayground, isTalkPresenter, isTransparentImage } from '@/pages/agents/utils';
import { useAuth } from '@/services/auth';
import { getPresenterFaceRect } from '@/services/images/crop.service';
import { logger } from '@/services/logger';
import { useAgentEditorStore } from '@/state/root';
import { FaceRect, VideoType } from '@d-id/types';
import { useEffect } from 'react';
import { useUpdateAgentLoader } from './useUpdateAgentLoader';

const DEFAULT_BACKGROUND_COLOR = '#ffffff';
const PLAYGROUND_STITCH_PADDING = 20;

interface Crop {
    cropX: number;
    cropY: number;
    cropWidth: number;
    cropHeight: number;
}

interface ImgSize {
    height: number;
    width: number;
}

export function updatePlaygroundCropImg(
    imgUrl: string,
    faceRect: FaceRect | undefined,
    imgSize: ImgSize,
    onUpdateDone?: () => void,
    bgColor: string = DEFAULT_BACKGROUND_COLOR
) {
    try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) {
            return;
        }

        canvas.width = CANVAS_WIDTH;
        canvas.height = CANVAS_HEIGHT;

        const adjustedCropResults: any = {};
        const crop: Partial<Crop> = {};

        if (!faceRect) {
            adjustedCropResults.x = (imgSize.width - (imgSize.height * canvas.width) / canvas.height) / 2;
            adjustedCropResults.y = 0;
            adjustedCropResults.width = imgSize.width - 2 * adjustedCropResults.x;
            adjustedCropResults.height = imgSize.height;
        } else {
            const padding = PLAYGROUND_STITCH_PADDING;
            crop.cropX = Math.max(faceRect.left - padding, 0);
            crop.cropY = Math.max(faceRect.top - padding, 0);
            crop.cropWidth = Math.min(faceRect.right - faceRect.left + 2 * padding, imgSize.width);
            crop.cropHeight = Math.min(faceRect.bottom - faceRect.top + 2 * padding, imgSize.height);
        }

        const onDoneProcessing = (imageUrl: string) => {
            displayInPlayground(imageUrl);
            onUpdateDone?.();
        };

        const posterImg = new Image(imgSize.width, imgSize.height);
        posterImg.crossOrigin = '';

        posterImg.onload = () => {
            const isTransparent = isTransparentImage(posterImg);

            if (!isTransparent) {
                return onDoneProcessing(imgUrl);
            }

            ctx.fillStyle = bgColor;
            ctx.fillRect(0, 0, canvas.width, canvas.height);

            if (faceRect) {
                if (!crop.cropWidth || !crop.cropHeight || crop.cropX === undefined || crop.cropY === undefined) {
                    console.error('no crop values but has faceRect');
                    return;
                }
                const cropAspectRatio = crop.cropWidth / crop.cropHeight;
                const canvasAspectRatio = canvas.width / canvas.height;

                let adjustedCropWidth = crop.cropWidth;
                let adjustedCropHeight = crop.cropHeight;

                if (canvasAspectRatio > cropAspectRatio) {
                    adjustedCropHeight = crop.cropHeight;
                    adjustedCropWidth = crop.cropHeight * canvasAspectRatio;
                } else {
                    adjustedCropWidth = crop.cropWidth;
                    adjustedCropHeight = crop.cropWidth / canvasAspectRatio;
                }

                const adjustedCropX = crop.cropX - (adjustedCropWidth - crop.cropWidth) / 2;
                const adjustedCropY = crop.cropY - (adjustedCropHeight - crop.cropHeight) / 2;

                adjustedCropResults.x = adjustedCropX;
                adjustedCropResults.y = adjustedCropY;
                adjustedCropResults.width = adjustedCropWidth;
                adjustedCropResults.height = adjustedCropHeight;
            }

            ctx.drawImage(
                posterImg,
                adjustedCropResults.x,
                adjustedCropResults.y,
                adjustedCropResults.width,
                adjustedCropResults.height,
                0,
                0,
                canvas.width,
                canvas.height
            );

            return onDoneProcessing(canvas.toDataURL('image/jpeg'));
        };

        posterImg.src = imgUrl;
    } catch (e) {
        onUpdateDone?.();
    }
}

export function useUpdatePresenterImg() {
    const agentEditorStore = useAgentEditorStore();
    const { isAnonymous } = useAuth();
    const { presenterBackgroundSourceUrl } = agentEditorStore;

    const presenter = useGetCurrentAgentsPresenter(false);
    const { showAgentLoader, hideAgentLoader } = useUpdateAgentLoader();

    useEffect(() => {
        if (isAnonymous || !presenter) {
            return;
        }

        const updatePlaygroundPresenterImg = async () => {
            try {
                if (!presenter) {
                    logger.error('no presenter to draw presenter img');
                    return;
                }

                const { type, poster, source_url: sourceUrl, thumbnail } = presenter;

                if (type === VideoType.CLIP && presenterBackgroundSourceUrl) {
                    return;
                }

                let imgUrl: string;
                const presenterPoster = poster;
                if (type === VideoType.CLIP) {
                    imgUrl = presenterPoster || thumbnail;
                } else if (type === VideoType.TALK) {
                    imgUrl = presenterBackgroundSourceUrl || presenterPoster || sourceUrl || thumbnail;
                } else {
                    logger.error('invalid presenter type for drawing presenter image, failing back to default', { presenter });
                    imgUrl = presenterPoster || sourceUrl || thumbnail;
                }

                if (!imgUrl) {
                    logger.error('no img url to draw presenter img');
                    return;
                }

                const shouldSkipDetection = !(isTalkPresenter(presenter) && !agentEditorStore.presenterIsStitch);
                const res = await getPresenterFaceRect({ imgUrl, shouldSkipDetection });

                if (!res) {
                    logger.error('Failed to get face crop for presenter');
                    return;
                }

                updatePlaygroundCropImg(
                    imgUrl,
                    res?.faceCrop,
                    {
                        width: res.metadata.width,
                        height: res.metadata.height,
                    },
                    () => {
                        agentEditorStore.setIsPlaygroundLoaded(true);
                        hideAgentLoader();
                    }
                );
            } catch (e) {
                logger.error('error drawing presenter img', e);
                if (agentEditorStore.isPlaygroundLoaded) {
                    hideAgentLoader();
                }
            }
        };

        showAgentLoader();
        updatePlaygroundPresenterImg();
    }, [presenter, agentEditorStore.presenterIsStitch]);
}
